import { useCallback, useEffect, useState } from 'react';
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import {
  BaseTransaction,
  PaymentType,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  TransactionWarning,
  TransactionWarningActionBody,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import StatusesUrl from '../../../types/statuses-url';
import {
  CheckTransaction,
  CheckTransactionStatus,
} from '../../../types/check-transaction';
import {
  useApplyOutgoingTransactionWarningActionMutation,
  useCheckOutgoingTransactionMutation,
  useCreateOutgoingTransactionMutation,
  useLazyGetOutgoingListQuery,
  useLazyGetOutgoingTransactionAmlByIdQuery,
  useLazyGetOutgoingTransactionByIdQuery,
  useLazyGetOutgoingTransactionWarningsQuery,
  useUpdateOutgoingTransactionMutation,
  useResetOutgoingTransactionWarningActionMutation,
  useLazyGetOutgoingTransactionWarningQuery,
  useLazyGetOutgoingTransactionWarningsInfoQuery,
  useLazyIbanValidationQuery,
  useLazyBicSearchQuery,
  useLazyBicValidationQuery,
  useLazyGetOutgoingIntegrationLogsQuery,
} from '../store/services';
import {
  BicSearch,
  BicValidation,
  IbanValidation,
  PSPDetails,
  UpdateOutgoingTransaction,
} from '../types';
import { setWarnings } from '../store/slices/transaction';
import { useLazyGetStatusesTransactionByIdQuery } from '../../../store/statuses/services';
import useRetry from '../helpers/use-retry';
import { useLazyGetTransactionsPurposeCodesSettingsQuery } from '../../../store/dictionaries/services';
import { PurposeCodeSettings } from '../../../store/types';

const bicRegex = /^[A-Z0-9]{4}[A-Z]{2}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/;

let bicValidationTimeout: NodeJS.Timeout;

const useRequests = ({
  setErrorMessage,
  setStatusChangeError,
  getValues,
  transactionId,
  setError,
  transaction,
  errors,
  warnings,
  setCheckError,
  fields,
  panelIsOpen,
  isDirty,
  setStatusCheckError,
  clearErrors,
  setValue,
  benificiaryBankCountry,
  intermediaryBankCountry,
  activePermissions,
  pspDetails,
  purposeCodeSettingItem,
  purposeCodeSettingSelectionType,
  setWarningActionError,
}: {
  setErrorMessage: (data: string) => void;
  setStatusChangeError: (data: string) => void;
  getValues: UseFormGetValues<any>;
  transactionId?: string;
  setError: UseFormSetError<any>;
  transaction?: BaseTransaction;
  errors: FieldErrors<any>;
  activePermissions?: PermissionEnum[];
  warnings?: TransactionWarning[];
  setCheckError: (data: string) => void;
  setStatusCheckError: (data: string) => void;
  fields?: any;
  panelIsOpen?: boolean;
  isDirty?: boolean;
  clearErrors: UseFormClearErrors<any>;
  setValue: UseFormSetValue<any>;
  benificiaryBankCountry?: string;
  intermediaryBankCountry?: string;
  pspDetails?: PSPDetails[];
  purposeCodeSettingItem?: PurposeCodeSettings;
  purposeCodeSettingSelectionType?: string;
  setWarningActionError: (
    data: { warningId: string; error: string } | undefined,
  ) => void;
}) => {
  const dispatch = useAppDispatch();

  const [feeValue, setFeeValue] = useState<number | undefined>(undefined);
  const [checkStatus, setCheckStatus] = useState<
    CheckTransactionStatus | undefined
  >(undefined);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [refundPopupVisible, setRefundPopupVisible] = useState(false);
  const [refundFormState, setRefundFormState] = useState<{
    refundedAt: string;
    refundFees: boolean;
    refundAmount: string;
  }>({
    refundedAt: '',
    refundFees: true,
    refundAmount: '',
  });
  const [refundAmountError, setRefundAmountError] = useState('');
  const [ibanValidationValues, setIbanValidationValues] = useState<
    IbanValidation | undefined
  >();
  const [beneficairyBicSearchResults, setBeneficiaryBicSearchResult] = useState<
    BicSearch[]
  >([]);
  const [intermediaryBicSearchResults, setIntermediaryBicSearchResult] =
    useState<BicSearch[]>([]);
  const [bicValidationBeneficiaryResult, setBicValidationBeneficiaryResult] =
    useState<BicValidation>();
  const [bicValidationIntermediaryResult, setBicValidationIntermediaryResult] =
    useState<BicValidation>();
  const [selectedValidationType, setSelectedValidationType] = useState('');
  const [selectedWarningId, setSelectedWarningId] = useState('');
  const [ibanRequestForbidden, setIbanRequestForbidden] = useState(false);
  const [bicRequestForbidden, setBicRequestForbidden] = useState(false);
  const [transferDateWithTime, setTransferDateWithTime] = useState(false);
  const [acceptedOutWithTime, setAcceptedOutWithTime] = useState(false);

  const { countriesList, purposeCodesSettings } = useAppSelector(
    (state) => state.transactionsDictionaries,
  );

  const [getOutgoingTransactionById, { isLoading, isUninitialized }] =
    useLazyGetOutgoingTransactionByIdQuery();
  const [update, { isLoading: updateIsLoading, isError: updateIsError }] =
    useUpdateOutgoingTransactionMutation();

  const [create, { isSuccess: createIsSuccess, isLoading: createIsLoading }] =
    useCreateOutgoingTransactionMutation();
  const [getTransactionsList] = useLazyGetOutgoingListQuery();
  const [
    getOutgoingTransactionWarnings,
    { isLoading: isWarningLoading, isFetching: isWarningFetching },
  ] = useLazyGetOutgoingTransactionWarningsQuery();
  const [
    getOutgoingTransactionWarningsInfo,
    { isLoading: isWarningInfoLoading },
  ] = useLazyGetOutgoingTransactionWarningsInfoQuery();
  const [
    getOutgoingTransactionWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetOutgoingTransactionWarningQuery();
  const [getOutgoingTransactionByIdAml, { isLoading: isLoadingAml }] =
    useLazyGetOutgoingTransactionAmlByIdQuery();
  const [
    getStatusesTransactionById,
    {
      isLoading: isLoadingStatuses,
      isFetching: isFetchingStatuses,
      isUninitialized: isUninitializedStatuses,
    },
  ] = useLazyGetStatusesTransactionByIdQuery();
  const [
    applyOutgoingTransactionWarning,
    { isLoading: applyTransactionsWarningIsLoading },
  ] = useApplyOutgoingTransactionWarningActionMutation();
  const [
    resetOutgoingTransactionWarning,
    {
      isLoading: resetTransactionsWarningIsLoading,
      isSuccess: resetTransactionsWarningIsSuccess,
    },
  ] = useResetOutgoingTransactionWarningActionMutation();
  const [
    ibanValidation,
    {
      isLoading: ibanValidationIsLoading,
      isFetching: ibanValidationIsFetching,
    },
  ] = useLazyIbanValidationQuery();
  const [
    bicSearch,
    { isLoading: bicSearchIsLoading, isFetching: bicSearchIsFetching },
  ] = useLazyBicSearchQuery();
  const [
    bicValidation,
    { isLoading: bicValidationIsLoading, isFetching: bicValidationIsFetching },
  ] = useLazyBicValidationQuery();
  const [
    getIntegrationLogs,
    {
      isLoading: integrationLogsIsLoading,
      isFetching: integrationLogsIsFetching,
    },
  ] = useLazyGetOutgoingIntegrationLogsQuery();
  const [getPurposeCodesSettings] =
    useLazyGetTransactionsPurposeCodesSettingsQuery();

  const getOutgoingTransactionWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    setSelectedWarningId(warningId);

    await getOutgoingTransactionWarningById({
      id,
      warningId,
    });
  };

  const { getTransaction, onClearTimer } = useRetry({
    getTransactionFunction: getOutgoingTransactionById,
    id: transactionId as string,
    isRetry: true,
    getStatusesFunction: getStatusesTransactionById,
    getTransactionWarningsFunction: getOutgoingTransactionWarnings,
    getTransactionWarningsInfoFunction: getOutgoingTransactionWarningsInfo,
    chatPermission: activePermissions?.includes(PermissionEnum.CHANNELS_LIST),
    statusPermission: activePermissions?.includes(
      PermissionEnum.TRANSACTIONS_OUTGOING_EDIT_ACTION,
    ),
    statusUrl: StatusesUrl.OUTGOING,
    warningsInfoPermission: activePermissions?.includes(
      PermissionEnum.TRANSACTIONS_OUTGOING_AML_LIST,
    ),
  });

  const onSearchBic = async (
    bankName: string,
    type: 'beneficiary' | 'intermediary',
  ) => {
    const body: {
      bankName: string;
      bankCountry?: string;
    } = {
      bankName,
    };

    if (type === 'beneficiary' && benificiaryBankCountry) {
      body.bankCountry = countriesList?.find(
        (item) => item.id === benificiaryBankCountry,
      )?.alpha2;
    } else if (type === 'intermediary' && intermediaryBankCountry) {
      body.bankCountry = countriesList?.find(
        (item) => item.id === intermediaryBankCountry,
      )?.alpha2;
    }

    const response = (await bicSearch(body)) as {
      data: { data: { items: BicSearch[] } };
      error?: ResponseError;
    };

    if (response?.data?.data) {
      if (type === 'beneficiary') {
        setBeneficiaryBicSearchResult(response?.data?.data?.items);
      } else if (type === 'intermediary') {
        setIntermediaryBicSearchResult(response?.data?.data?.items);
      }
    } else if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setErrorMessage,
      });
    }
  };

  const onBicValidate = async (
    bic: string,
    type: 'beneficiary' | 'intermediary',
  ) => {
    setSelectedValidationType(type);

    if (
      (pspDetails?.find(
        (item) =>
          item.key === 'IBAN-COM-BIC-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
      )?.data?.value as boolean) &&
      activePermissions?.includes(PermissionEnum.IBAN_BIC_VALIDATE_ACTION)
    ) {
      if (bicRegex.test(bic)) {
        const response = (await bicValidation(bic)) as {
          data: { data: BicValidation };
          error?: ResponseError;
        };

        if (response?.error) {
          if (response?.error?.status === 403) {
            setBicRequestForbidden(true);
          }

          responseErrorsResolver({
            errors: response.error,
            setError,
            setGlobalError: setErrorMessage,
          });
        } else if (!response.error && response?.data?.data) {
          setBicRequestForbidden(false);

          if (type === 'beneficiary') {
            clearErrors(['bic']);
            setBicValidationBeneficiaryResult(response.data.data);
          } else if (type === 'intermediary') {
            setBicValidationIntermediaryResult(response.data.data);
          }
        }
      }
    } else {
      clearTimeout(bicValidationTimeout);

      const cleanBic = bic.replace(/\s+/g, '').toUpperCase();

      bicValidationTimeout = setTimeout(() => {
        const valid = bicRegex.test(cleanBic);

        if (type === 'beneficiary') {
          if (bic) {
            if (!valid) {
              setError('benificiaryBIC', {
                type: 'custom',
                message: 'BIC is Invalid',
              });
            } else {
              clearErrors('benificiaryBIC');
            }
          } else {
            clearErrors('benificiaryBIC');
          }
        }

        if (type === 'intermediary') {
          if (bic) {
            if (!valid) {
              setError('intermediaryBankBIC', {
                type: 'custom',
                message: 'BIC is Invalid',
              });
            } else {
              clearErrors('intermediaryBankBIC');
            }
          } else {
            clearErrors('intermediaryBankBIC');
          }
        }
      }, 500);
    }
  };

  const onValidateIban = async (iban: string) => {
    setIbanValidationValues(undefined);
    setValue('benificiaryBIC', '');
    setValue('benificiaryBankName', '');
    setValue('benificiaryBankCountry', '');
    setValue('benificiaryBankAddress', '');

    const response = (await ibanValidation(iban)) as {
      data: { data: IbanValidation };
      error?: ResponseError;
    };

    if (response?.error) {
      if (response?.error?.status === 403) {
        setIbanRequestForbidden(true);
      }

      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError: setErrorMessage,
      });
    } else if (response?.data?.data && !response.error) {
      const { data } = response.data;

      setIbanRequestForbidden(false);
      setIbanValidationValues(data);
      clearErrors(['iban']);
      setErrorMessage('');

      if (data.bic) {
        setValue('benificiaryBIC', data.bic);
      }

      if (data.bank) {
        setValue('benificiaryBankName', data.bank);
      }

      if (data.country || data.countryIso) {
        const currentCountry = countriesList?.find(
          (item) =>
            item.name === data?.country || item.alpha2 === data?.countryIso,
        );

        if (currentCountry) {
          setValue('benificiaryBankCountry', currentCountry?.id);
        }
      }

      if (data?.address) {
        setValue('benificiaryBankAddress', `${data.city}, ${data.address}`);
      }

      if (
        fields?.paymentType === PaymentType.SWIFT &&
        bicValidationBeneficiaryResult
      ) {
        setBicValidationBeneficiaryResult(undefined);
      }
    }
  };

  const onResetTransactionWarningAction = async (warningId: string) => {
    const response = (await resetOutgoingTransactionWarning({
      warningId,
      transactionId: transactionId as string,
    })) as { error: ResponseError; data?: { data: TransactionWarning } };

    if (response && !response.error) {
      const nextWarnings = warnings?.map((item) => {
        const result = { ...item };

        if (result.id === response?.data?.data?.id) {
          result.action = response.data.data.action;
          result.matchStatus = response.data.data.matchStatus;
        }

        return result;
      });

      getOutgoingTransactionWarningsInfo(transactionId as string);

      dispatch(setWarnings(nextWarnings as TransactionWarning[]));
    }
  };

  const onChangeRefundState = ({
    name,
    value,
  }: {
    name: string;
    value: string | boolean;
  }) => {
    const nextRefundFormState = { ...refundFormState };

    // @ts-ignore
    nextRefundFormState[name] = value;

    setRefundFormState(nextRefundFormState);
  };

  const onSubmit = async () => {
    let response;

    if (!Object.keys(errors).length) {
      setErrorMessage('');
      setStatusChangeError('');
      const values = getValues();

      const body: UpdateOutgoingTransaction = {
        chargeType: values.chargeType || undefined,
        paymentType: values.paymentType || undefined,
        pspPartnerId: values.pspPartnerId || undefined,
        pspPartnerAccountNumber: values.pspPartnerAccountNumber || undefined,
        documentNumber: values.documentNumber || undefined,
        info: values.info || undefined,
      };

      if (values?.acceptedOut) {
        const date = new Date(values.acceptedOut);

        if (acceptedOutWithTime) {
          body.acceptedOut = date.toUTCString();
        } else {
          body.acceptedOut = values.acceptedOut;
        }
      } else {
        body.acceptedOut = undefined;
      }

      if (values.collectorContact) {
        body.collectorContact = values.collectorContact;
      }

      if (values.customPassword) {
        body.customPassword = values.customPassword;
      }

      response = (await update({ body, id: transactionId as string })) as {
        data?: BaseTransaction;
        error: ResponseError;
      };

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setErrorMessage,
        });
      } else {
        getStatusesTransactionById({
          id: transactionId as string,
          url: StatusesUrl.OUTGOING,
          baseellaStatusFrom: transaction?.latestQueuedStatuses
            ?.baseellaStatus as string,
        });
      }
    }

    return response;
  };

  const getOutgoingTransactionByIdAmlAction = () => {
    getOutgoingTransactionByIdAml(transactionId as string);
  };

  const onApplyTransactionAction = async ({
    body,
    warningId,
  }: {
    body: TransactionWarningActionBody;
    warningId: string;
  }) => {
    setWarningActionError(undefined);

    const response = (await applyOutgoingTransactionWarning({
      transactionId: transactionId as string,
      body,
      warningId,
    })) as { error: ResponseError; data?: { data: TransactionWarning } };

    if (response && !response.error) {
      const nextWarnings = warnings
        ?.map((item) => {
          const result = { ...item };

          if (result.id === response?.data?.data?.id) {
            result.action = response.data.data.action;
            result.matchStatus = response.data.data.matchStatus;
          }

          return result;
        })
        ?.filter((item) => !item.action);

      getOutgoingTransactionWarningsInfo(transactionId as string);

      if (!nextWarnings?.length) {
        getStatusesTransactionById({
          id: transactionId as string,
          url: StatusesUrl.OUTGOING,
          baseellaStatusFrom: transaction?.latestQueuedStatuses
            ?.baseellaStatus as string,
        });
      }
    } else if (response?.error) {
      setWarningActionError({
        warningId,
        error: response?.error?.data?.message as string,
      });
    }
  };

  const [checkPayment, { isLoading: checkInProgress }] =
    useCheckOutgoingTransactionMutation();

  const abortController = (() => {
    let controller = new AbortController();
    let controllerSignal = controller.signal;

    const abortPendingRequest = () => {
      controller.abort();
      // eslint-disable-next-line react-hooks/immutability
      controller = new AbortController();
      controllerSignal = controller.signal;
    };

    const getSignalToken = () => controllerSignal;

    return {
      abortPendingRequest,
      getSignalToken,
    };
  })();

  const checkPaymentAction = useCallback(async () => {
    abortController.abortPendingRequest();

    setCheckError('');
    setStatusCheckError('');
    setErrorMessage('');
    clearErrors(['sum', 'caspId']);

    const body: CheckTransaction = {
      paymentType: fields.paymentType,
      sum: Number(fields.sum),
      senderAccountNumber: fields.senderAccountNumber,
      recipientAccountNumber: fields.recipientAccountNumber || '',
      currencyId: fields.currencyId,
      info: fields.info,
    };

    if (fields.caspId) {
      body.caspId = fields.caspId;
    }

    if (fields.purposeCode && purposeCodeSettingItem) {
      body.purposeCode = fields.purposeCode;
    }

    if (fields.benificiaryBankCountry) {
      body.benificiaryBankCountry = fields.benificiaryBankCountry;
    }

    const response = (await checkPayment({ body, abortController })) as {
      data?: any;
      error?: { data: { message?: string }; message?: string } | ResponseError;
    };

    if ('data' in response) {
      setFeeValue(response.data.data.totalFee);
      setCheckStatus(response.data.data.status);

      if (response?.data?.data?.errorMessage) {
        setStatusCheckError(response?.data?.data?.errorMessage);
      }

      if (response.data.data.status === CheckTransactionStatus.GREEN) {
        setSubmitDisabled(false);
      }
    } else {
      setSubmitDisabled(true);

      if (response && response.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError: setCheckError,
        });
      }
    }
  }, [
    checkPayment,
    fields.paymentType,
    fields.sum,
    fields.senderAccountNumber,
    fields.recipientAccountNumber,
    fields.currencyId,
    fields.caspId,
    fields.info,
    fields.benificiaryBankCountry,
    fields.purposeCode,
    purposeCodeSettingItem,
  ]);

  useEffect(() => {
    if (
      !purposeCodesSettings &&
      activePermissions?.includes(PermissionEnum.PURPOSE_CODE_SETTINGS_LIST)
    ) {
      getPurposeCodesSettings();
    }
  }, [purposeCodesSettings, activePermissions]);

  useEffect(() => {
    if (transaction) {
      setRefundFormState({
        ...refundFormState,
        refundFees: transaction.refundFees,
      });
    }
  }, [transaction]);

  useEffect(() => {
    if (panelIsOpen && transactionId) {
      getTransaction();

      setTransferDateWithTime(true);
      setAcceptedOutWithTime(true);
    }
  }, [panelIsOpen, transactionId]);

  useEffect(() => {
    if (
      !transactionId &&
      isDirty &&
      fields.paymentType &&
      fields.paymentType === 'CASH_OVER_COUNTER' &&
      fields.sum &&
      fields.currencyId &&
      fields.benificiaryFullName &&
      fields.collectorContact &&
      fields.info
    ) {
      checkPaymentAction();
    }
  }, [
    isDirty,
    transaction,
    fields.paymentType,
    fields.sum,
    fields.currencyId,
    fields.benificiaryFullName,
    fields.collectorContact,
    fields.info,
  ]);

  useEffect(() => {
    if (
      !transactionId &&
      isDirty &&
      fields.paymentType &&
      fields.sum.toString() &&
      fields.senderAccountNumber &&
      fields.recipientAccountNumber &&
      fields.currencyId &&
      fields.info
    ) {
      if (fields.paymentType !== 'CRYPTO_ASSET_PAYMENT') {
        if (
          ((!pspDetails?.find(
            (item) =>
              item.key === 'IBAN-COM-IBAN-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
          )?.data?.value ||
            ((pspDetails?.find(
              (item) =>
                item.key === 'IBAN-COM-IBAN-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
            )?.data?.value as boolean) &&
              !activePermissions?.includes(
                PermissionEnum.IBAN_VALIDATE_ACTION,
              ))) &&
            (!pspDetails?.find(
              (item) =>
                item.key === 'IBAN-COM-BIC-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
            )?.data?.value ||
              ((pspDetails?.find(
                (item) =>
                  item.key ===
                  'IBAN-COM-BIC-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
              )?.data?.value as boolean) &&
                !activePermissions?.includes(
                  PermissionEnum.IBAN_BIC_VALIDATE_ACTION,
                )))) ||
          (fields.paymentType !== PaymentType.SEPA_SCT &&
            fields.paymentType !== PaymentType.SEPA_SCT_INST &&
            fields.paymentType !== PaymentType.SWIFT)
        ) {
          if (!purposeCodeSettingItem) {
            checkPaymentAction();
          } else if (purposeCodeSettingItem) {
            if (
              purposeCodeSettingSelectionType === 'currency' &&
              fields.purposeCode
            ) {
              checkPaymentAction();
            } else if (
              purposeCodeSettingSelectionType === 'country' &&
              fields.benificiaryBankCountry &&
              fields.purposeCode
            ) {
              checkPaymentAction();
            }
          }
        } else if (
          ((pspDetails?.find(
            (item) =>
              item.key === 'IBAN-COM-IBAN-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
          )?.data?.value as boolean) &&
            activePermissions?.includes(PermissionEnum.IBAN_VALIDATE_ACTION) &&
            (fields?.paymentType === PaymentType.SEPA_SCT ||
              fields?.paymentType === PaymentType.SEPA_SCT_INST) &&
            ibanValidationValues &&
            ibanValidationValues.valid) ||
          ((pspDetails?.find(
            (item) =>
              item.key === 'IBAN-COM-BIC-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
          )?.data?.value as boolean) &&
            (activePermissions?.includes(
              PermissionEnum.IBAN_BIC_SEARCH_ACTION,
            ) ||
              activePermissions?.includes(
                PermissionEnum.IBAN_BIC_VALIDATE_ACTION,
              )) &&
            ((bicValidationBeneficiaryResult &&
              bicValidationBeneficiaryResult.bicValid) ||
              (ibanValidationValues && ibanValidationValues.valid)) &&
            fields.paymentType === PaymentType.SWIFT) ||
          ((pspDetails?.find(
            (item) =>
              item.key === 'IBAN-COM-IBAN-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
          )?.data?.value as boolean) &&
            activePermissions?.includes(PermissionEnum.IBAN_VALIDATE_ACTION) &&
            (fields?.paymentType === PaymentType.SEPA_SCT ||
              fields?.paymentType === PaymentType.SEPA_SCT_INST) &&
            ibanRequestForbidden) ||
          ((pspDetails?.find(
            (item) =>
              item.key === 'IBAN-COM-BIC-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
          )?.data?.value as boolean) &&
            (activePermissions?.includes(
              PermissionEnum.IBAN_BIC_SEARCH_ACTION,
            ) ||
              activePermissions?.includes(
                PermissionEnum.IBAN_BIC_VALIDATE_ACTION,
              )) &&
            fields.paymentType === PaymentType.SWIFT &&
            bicRequestForbidden)
        ) {
          if (!purposeCodeSettingItem) {
            checkPaymentAction();
          } else if (purposeCodeSettingItem) {
            if (
              purposeCodeSettingSelectionType === 'currency' &&
              fields.purposeCode
            ) {
              checkPaymentAction();
            } else if (
              purposeCodeSettingSelectionType === 'country' &&
              fields.benificiaryBankCountry &&
              fields.purposeCode
            ) {
              checkPaymentAction();
            }
          }
        }
      } else if (fields.paymentType === 'CRYPTO_ASSET_PAYMENT') {
        if (fields.caspId) {
          checkPaymentAction();
        }
      }
    }
  }, [
    isDirty,
    checkPaymentAction,
    fields.paymentType,
    fields.sum,
    fields.senderAccountNumber,
    fields.recipientAccountNumber,
    fields.currencyId,
    fields?.caspId,
    transactionId,
    fields.info,
    ibanValidationValues,
    activePermissions,
    pspDetails,
    bicValidationBeneficiaryResult,
    ibanRequestForbidden,
    bicRequestForbidden,
    purposeCodeSettingItem,
    fields.purposeCode,
    fields.benificiaryBankCountry,
  ]);

  return {
    feeValue,
    setFeeValue,
    submitDisabled,
    setSubmitDisabled,
    checkStatus,
    setCheckStatus,
    isLoading,
    isUninitialized,
    updateIsLoading,
    updateIsError,
    create,
    createIsSuccess,
    createIsLoading,
    getTransactionsList,
    isWarningLoading,
    isWarningFetching,
    isLoadingAml,
    isLoadingStatuses,
    isFetchingStatuses,
    isUninitializedStatuses,
    applyTransactionsWarningIsLoading,
    onClearTimer,
    onSubmit,
    getOutgoingTransactionByIdAmlAction,
    onApplyTransactionAction,
    checkInProgress,
    getTransaction,
    getStatusesTransactionById,
    refundPopupVisible,
    setRefundPopupVisible,
    onChangeRefundState,
    refundFormState,
    refundAmountError,
    setRefundAmountError,
    onResetTransactionWarningAction,
    resetTransactionsWarningIsLoading,
    resetTransactionsWarningIsSuccess,
    getOutgoingTransactionWarningByIdAction,
    isWarningByIdLoading,
    isWarningByIdFetching,
    isWarningInfoLoading,
    onValidateIban,
    ibanValidationIsLoading:
      ibanValidationIsLoading || ibanValidationIsFetching,
    ibanValidationValues,
    onSearchBic,
    onBicValidate,
    bicSearchIsLoading: bicSearchIsLoading || bicSearchIsFetching,
    beneficairyBicSearchResults,
    intermediaryBicSearchResults,
    bicValidationIsLoading: bicValidationIsLoading || bicValidationIsFetching,
    bicValidationBeneficiaryResult,
    bicValidationIntermediaryResult,
    selectedValidationType,
    setBicValidationBeneficiaryResult,
    setIbanValidationValues,
    selectedWarningId,
    bicRequestForbidden,
    ibanRequestForbidden,
    getIntegrationLogs,
    integrationLogsIsLoading,
    integrationLogsIsFetching,
    transferDateWithTime,
    acceptedOutWithTime,
    setTransferDateWithTime,
    setAcceptedOutWithTime,
  };
};

export default useRequests;
